if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
      }

      init() {
        console.log('MediaGallery initializing...', this.elements);
        
        // Initialize slider even without thumbnails
        if (this.elements.viewer) {
          console.log('Viewer found:', this.elements.viewer);
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          
          // Add click handlers to slider buttons
          const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
          const nextButton = this.elements.viewer.querySelector('button[name="next"]');
          
          console.log('Slider buttons found:', { prevButton, nextButton });
          
          if (prevButton) {
            prevButton.addEventListener('click', (e) => {
              e.preventDefault();
              console.log('Previous button clicked');
              this.goToPreviousSlide();
            });
          }
          
          if (nextButton) {
            nextButton.addEventListener('click', (e) => {
              e.preventDefault();
              console.log('Next button clicked');
              this.goToNextSlide();
            });
          }

          // Initialize counter
          const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
          this.updateCounter(1, allSlides.length);
        }

        if (this.elements.thumbnails) {
          console.log('Thumbnails found:', this.elements.thumbnails);
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (button) {
              button.addEventListener('click', (e) => {
                e.preventDefault();
                console.log('Thumbnail clicked:', mediaToSwitch.dataset.target);
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
              });
            }
          });
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        console.log('setActiveMedia called with:', mediaId);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          console.log('No active media found for:', mediaId);
          return;
        }
        
        console.log('Setting active media:', activeMedia);
        
        // Remove active class from all media items
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Add active class to the selected media
        activeMedia.classList.add('is-active');
        
        // Force visibility
        activeMedia.style.display = 'block';
        activeMedia.style.visibility = 'visible';
        activeMedia.style.opacity = '1';

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Update slider position and counter
        this.updateSliderPosition(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      updateSliderPosition(activeMedia) {
        const sliderComponent = this.elements.viewer;
        if (!sliderComponent) return;

        // Find the index of the active media
        const allSlides = sliderComponent.querySelectorAll('[data-media-id]');
        let activeIndex = 0;
        allSlides.forEach((slide, index) => {
          if (slide === activeMedia) {
            activeIndex = index;
          }
        });

        console.log('Updating slider position for index:', activeIndex);

        // Update the slider position
        if (sliderComponent.slider && sliderComponent.sliderItemOffset) {
          const newPosition = activeIndex * sliderComponent.sliderItemOffset;
          sliderComponent.slider.scrollTo({ left: newPosition });
          
          // Trigger the slider update to refresh counter
          if (sliderComponent.update) {
            sliderComponent.update();
          }
        } else {
          // Fallback: scroll the media list directly
          const mediaList = sliderComponent.querySelector('.product__media-list');
          if (mediaList) {
            const slideWidth = activeMedia.offsetWidth || mediaList.offsetWidth;
            const newPosition = activeIndex * slideWidth;
            console.log('Scrolling to position:', newPosition, 'slideWidth:', slideWidth);
            mediaList.scrollTo({ left: newPosition, behavior: 'smooth' });
          }
        }

        // Update counter manually
        this.updateCounter(activeIndex + 1, allSlides.length);
        
        // Ensure the active media is visible
        setTimeout(() => {
          activeMedia.style.display = 'block';
          activeMedia.style.visibility = 'visible';
          activeMedia.style.opacity = '1';
          
          // Debug visibility
          const computedStyle = window.getComputedStyle(activeMedia);
          console.log('Active media visibility:', {
            display: computedStyle.display,
            visibility: computedStyle.visibility,
            opacity: computedStyle.opacity,
            element: activeMedia
          });
        }, 100);
      }

      updateCounter(current, total) {
        const currentElement = this.elements.viewer.querySelector('.slider-counter--current');
        const totalElement = this.elements.viewer.querySelector('.slider-counter--total');
        
        if (currentElement) {
          currentElement.textContent = current;
        }
        if (totalElement) {
          totalElement.textContent = total;
        }
        
        console.log('Counter updated:', current, 'of', total);
      }

      goToPreviousSlide() {
        console.log('goToPreviousSlide called');
        const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        console.log('All slides:', allSlides.length, 'Active slide:', activeSlide);
        
        if (!activeSlide) return;

        let currentIndex = 0;
        allSlides.forEach((slide, index) => {
          if (slide === activeSlide) {
            currentIndex = index;
          }
        });

        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allSlides.length - 1;
        const prevSlide = allSlides[prevIndex];
        console.log('Going to previous slide:', prevIndex, prevSlide);
        if (prevSlide) {
          this.setActiveMedia(prevSlide.dataset.mediaId, false);
        }
      }

      goToNextSlide() {
        console.log('goToNextSlide called');
        const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        console.log('All slides:', allSlides.length, 'Active slide:', activeSlide);
        
        if (!activeSlide) return;

        let currentIndex = 0;
        allSlides.forEach((slide, index) => {
          if (slide === activeSlide) {
            currentIndex = index;
          }
        });

        const nextIndex = currentIndex < allSlides.length - 1 ? currentIndex + 1 : 0;
        const nextSlide = allSlides[nextIndex];
        console.log('Going to next slide:', nextIndex, nextSlide);
        if (nextSlide) {
          this.setActiveMedia(nextSlide.dataset.mediaId, false);
        }
      }
    }
  );
}
